/* eslint-disable @typescript-eslint/no-unused-vars */
import { ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../controls/Button';
import GroupsTable from '../../GroupsTable';
import { ActionData, Group } from '../../../constants/telosair-interfaces';
import AddGroupModal from '../../../modals/AddGroup';
import AuthHeader from '../header';
import { useData } from '../../../context/data.context';
import { deleteGroup } from '../../../services/dashboard-service';
import { GA_CATEGORIES, sendCustomEventToGa } from '../../../_helpers/ga';

/**
 * Group setup component for account activation.
 * @returns - React compoennt for group setup.
 */
export default function AccountActivationGroupSetup(): ReactElement {
  const navigate = useNavigate();
  const { groupedData, devices, fetchAllGroups } = useData();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionData, setActionData] = useState<ActionData<Group>>({} as ActionData<Group>);
  /**
   * Function to handle Device Modal.
   * @param newDeviceAdded - To check weather a New device is added or not.
   */
  const handleDeviceModalClose = (newDeviceAdded: boolean): void => {
    if (newDeviceAdded) {
      // refresh();
    }
    setIsModalOpen(false);
  };

  /**
   * Function to delete a group.
   * @param id - ID of the group.
   */
  const handleDeleteGroup = async (id: number): Promise<void> => {
    try {
      setLoading(true);
      await deleteGroup(id);
      await fetchAllGroups();
      setLoading(false);
      sendCustomEventToGa(GA_CATEGORIES.onboarding, 'group-deletion-completed');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      sendCustomEventToGa(GA_CATEGORIES.onboarding, 'group-deletion-failed');
    }
  };

  /**
   * Function to operate delete and edit group.
   * @param action - Action data needs type and group data.
   */
  const handleAction = async (action: ActionData<Group>): Promise<void> => {
    if (action.type === 'delete') {
      handleDeleteGroup(action.data.groupId);
      sendCustomEventToGa(GA_CATEGORIES.onboarding, 'group-deletion-initiated');
    } else {
      setActionData(action);
      setIsModalOpen(true);
      sendCustomEventToGa(GA_CATEGORIES.onboarding, 'group-editing-initiated');
    }
  };

  /**
   * Handle close the Create Group modal.
   * @param newData - Check weather new group is created.
   */
  const handleGroupModalClose = (newData?: boolean): void => {
    if (actionData.type === 'edit') {
      sendCustomEventToGa(GA_CATEGORIES.onboarding, 'group-editing-completed');
    }
    if (newData) {
      fetchAllGroups();
      sendCustomEventToGa(GA_CATEGORIES.onboarding, 'group-added');
    }
    sendCustomEventToGa(GA_CATEGORIES.onboarding, 'group-modal-closed');
    setIsModalOpen(false);
  };

  return (
    <div className="h-screen flex flex-col flex-1">
      <div className="flex flex-col justify-between sticky top-0 z-20">
        <AuthHeader />
        <div className="w-2/3 border-t-4 border-t-primary-500" />
      </div>
      <div className="flex flex-1 flex-col md:block w-full">
        <div className="md:grid flex flex-col flex-1 md:container grid-cols-12 py-10 px-3 md:px-16 md:mx-auto h-full gap-3">
          <div className="col-span-2 w-full md:w-auto flex justify-center items-center md:items-start md:justify-start">
            <div className="w-4/5">
              <Button
                onClick={() => navigate(-1)}
                isBlockButton
                theme="secondary-color"
                size="sm"
                name="Go Back"
              />
            </div>
          </div>
          <div className="md:col-span-8 col-span-12 bg-container-700 p-6 rounded h-full">
            <div className="text-primary-500 text-2xl font-bold">
              Step 2 of 3: <span className="text-white">Group Setup</span>
            </div>
            <div className="text-gray-400 mt-1 leading-6">
              Adding groups will help you to arrange and monitor devices easily
            </div>
            <div className="flex justify-between items-center mt-8 text-gray-400 text-sm">
              <div>
                Group : <span className="text-gray-300">{groupedData?.length || '0'} Groups</span> |
                Selected: <span className="text-gray-300">0 Groups</span>
              </div>
              <div>
                <Button
                  onClick={() => {
                    setActionData({} as ActionData<Group>);
                    setIsModalOpen(true);
                  }}
                  theme="secondary-gray"
                  size="sm"
                  name="Add Group"
                />
              </div>
            </div>
            <div className="mt-5">
              <GroupsTable
                setIsGroupModalOpen={(isOpen) => setIsModalOpen(isOpen)}
                handleAction={handleAction}
                groups={groupedData}
              />
            </div>
          </div>
          <div className="col-span-2 flex-col md:items-end flex w-full md:w-auto items-center">
            <div className="w-4/5">
              <Button
                onClick={() => navigate('/account-activation/invite-users')}
                isBlockButton
                theme="primary"
                size="sm"
                name="Confirm Groups"
              />
            </div>
            <div className="w-4/5 mt-3">
              <Button
                onClick={() => navigate('/account-activation/invite-users')}
                isBlockButton
                theme="secondary-color"
                size="sm"
                name="Skip setup"
              />
            </div>
          </div>
        </div>
      </div>
      {isModalOpen && <AddGroupModal actionData={actionData} handleClose={handleGroupModalClose} />}
    </div>
  );
}
